import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

const ProspectList = React.lazy(() => import("./candidate/ProspectList"));
const ProspectDetail = React.lazy(() => import("./candidate/ProspectDetail"));
const NewProspect = React.lazy(() => import("./candidate/NewProspect"));
const FormsAdmin = React.lazy(() => import("./forms/FormsAdmin"));
const NotificationRules = React.lazy(() => import("./NotificationRules"));

/**
 * Prospecting Module Config
 *
 * Permissions resolved from backend (src/modules/prospecting/permissions.ts)
 * via the modulePermissions GraphQL query — empty arrays below are filled at
 * runtime by ModulePermissionsContext.
 *
 * The public pre-screening form (`/prospecting/form/:slug`) is NOT registered
 * here — it's a public route added directly in App.tsx outside ProtectedRoute.
 */
const prospectingModuleConfig: ModuleConfig = {
  id: "prospecting",
  name: "Recruitment",
  description:
    "Recruitment pipeline — application forms, phone pre-screen, Meet interviews, referee links, credential checklist, and the put-forward hand-over to the employer",
  version: "0.2.0",
  enabled: true,
  category: "operations",
  order: 50,
  icon: "🧑‍💼",
  color: "#9f7aea",

  routes: [
    {
      path: "/prospecting",
      component: ProspectList,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/prospecting/new",
      component: NewProspect,
      permissionLevel: "create",
      useLayout: true,
    },
    {
      path: "/prospecting/forms",
      component: FormsAdmin,
      permissionLevel: "chaseAdmin",
      useLayout: true,
    },
    {
      path: "/prospecting/notifications",
      component: NotificationRules,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/prospecting/:id",
      component: ProspectDetail,
      permissionLevel: "view",
      useLayout: true,
    },
  ],

  navigation: [
    {
      label: "Pipeline",
      path: "/prospecting",
      icon: "🧑‍💼",
      permissionLevel: "view",
    },
    {
      label: "Add Candidate",
      path: "/prospecting/new",
      icon: "➕",
      permissionLevel: "create",
    },
    {
      label: "Application forms",
      path: "/prospecting/forms",
      icon: "📝",
      permissionLevel: "chaseAdmin",
    },
    {
      label: "Notifications",
      path: "/prospecting/notifications",
      icon: "🔔",
      permissionLevel: "view",
    },
  ],

  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },

  quickActions: [
    {
      label: "Add Candidate",
      path: "/prospecting/new",
      icon: "➕",
      description: "Add a new prospect to the pipeline",
    },
    {
      label: "Pipeline",
      path: "/prospecting",
      icon: "🧑‍💼",
      description: "Board of every candidate by stage",
    },
  ],

  changelog: [
    {
      version: "0.2.0",
      releaseDate: "2026-09-03",
      title: "Recruitment pipeline — TalentLink+ vets, Paradise Lakes onboards",
      features: [
        "New ladder everywhere: Applied → Pre-screen → Interview scheduled → Interview done → Reference check → Document check → Suitable → Put forward → Placed (+ Rejected / Withdrawn); legacy stages render as their canonical stage (utils/stages.ts)",
        "Board / list pipeline with stage, position and company filters; re-applicant flag from previousApplications",
        "Stage moves through advanceCandidateStage(id, stage, reason); the server's refusal (SUITABLE blockers) is shown verbatim",
        "Candidate detail tabs: Overview · Pre-screen (completePreScreen, form preScreenQuestions) · Interview (Meet room auto-created when the URL is blank; completeInterview) · References (requestReference PHONE/LINK/EMAIL, recordPhoneReference, n of m) · Documents (server credentialChecklist, CID-only uploads via setCandidateCredential, assessPoliceCheck) · Activity (candidateActivity + addCandidateNote)",
        "Put forward: SUITABLE-only, company picker defaulting to the candidate's company, confirmation of what is sent, result shows employer / notified / created vs refreshed; DECLINED_BY_COMPANY surfaced on the record",
        "Public referee page /prospecting/reference/:token (publicReferenceByToken → submitReferenceByToken), mobile first",
        "Application form: earliest start (__earliestStart, YYYY-MM-DD), position from the form, working-rights hint; form admin exposes positionTitle / roleCategory / companyId / earliestStartRequired / minReferences / pre-screen questions (updateCandidateFormPosition)",
        "Two Brisbane date fixes: ScheduleInterviewModal and CandidateDraftsThread pin datetime-local values to +10:00 and display through utils/brisbane.ts",
      ],
      notes:
        "Frontend for Workstream A of the 2026-09-03 recruitment-pipeline plan. Backend module doc: business-builder-backend/src/modules/prospecting/CLAUDE.md.",
    },
    {
      version: "0.1.0",
      releaseDate: "2026-04-13",
      title: "Phase 1 — Pre-screening MVP",
      features: [
        "Candidate CRUD with pipeline stages (CV received → screening → shortlist → interview → offer → hired/rejected)",
        "Slug-based public pre-screening form — no auth needed for candidates to fill",
        "SMS + email invite send via per-tenant Postmark + Cellcast config",
        "Seeded default forms for PC + Maintenance roles",
        "Stage auto-advance on form submission (SCREENING_SENT → SCREENING_REPLIED)",
      ],
      notes:
        "Built same-day for Hao's PC + maintenance candidates on 2026-04-13. Phase 2 adds AI-drafted chase outreach + calendar-scheduled delivery.",
    },
  ],

  roadmap: [
    {
      title: "Phase 2 — AI Chase Cockpit",
      description:
        "Clone tom-manager's chase pattern into prospecting/entities/chase — candidate-specific AI drafting + calendar scheduling",
      priority: "high",
      status: "planned",
      features: [
        "Per-stage goal map driving AI prompts (e.g. `SCREENING_SENT → 'get them to fill the form'`)",
        "Multi-brand send preview",
        "Calendar-scheduled delivery via existing `calendarBroadcast.ts` cron",
        "Refine with AI feedback loop",
        "Snooze + manual milestone marking",
      ],
    },
    {
      title: "Phase 3 — Hire Conversion + Two-Way Messaging",
      description: "Candidate → Client + Employee on hire; inbound reply routing",
      priority: "high",
      status: "planned",
      features: [
        "`hireCandidate` atomic mutation",
        "Postmark + Cellcast inbound webhook handlers",
        "Reply threads on candidate record",
      ],
    },
    {
      title: "Phase 4 — Polish + Compliance",
      description:
        "Stage CRUD + kanban UI, interview scheduling, right-to-work + reference check automation, form builder",
      priority: "medium",
      status: "planned",
      features: [
        "Drag-drop kanban across stages",
        "Interview scheduler tied to calendar module",
        "Reference check automation",
        "Right-to-work verification workflow",
        "Per-role form builder UI",
      ],
    },
  ],
};

export default prospectingModuleConfig;
